import { useCallback, useRef, useState } from "react";

/**
 * Handle-based drag reorder that works with mouse AND touch (pointer events).
 *
 * Usage:
 *   const { listRef, dragIndex, overIndex, getHandleProps } = useHandleReorder(onReorder);
 *   <div ref={listRef}>
 *     {items.map((item, i) => (
 *       <div data-reorder-index={i}>
 *         <div {...getHandleProps(i)} />
 *       </div>
 *     ))}
 *   </div>
 *
 * `onReorder(fromIndex, toIndex)` fires on drop when the index changed.
 */
export default function useHandleReorder(onReorder) {
  const listRef = useRef(null);
  const dragRef = useRef({ from: null, over: null });
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const indexAtPoint = useCallback((x, y) => {
    const el = document.elementFromPoint(x, y)?.closest("[data-reorder-index]");
    if (!el || !listRef.current?.contains(el)) return null;
    const idx = Number(el.dataset.reorderIndex);
    return Number.isNaN(idx) ? null : idx;
  }, []);

  const finish = useCallback(() => {
    const { from, over } = dragRef.current;
    dragRef.current = { from: null, over: null };
    setDragIndex(null);
    setOverIndex(null);
    if (from !== null && over !== null && from !== over) onReorder(from, over);
  }, [onReorder]);

  const getHandleProps = useCallback(
    (index) => ({
      role: "button",
      "aria-label": "גרור לשינוי סדר",
      style: { touchAction: "none" },
      onPointerDown: (e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        dragRef.current = { from: index, over: index };
        setDragIndex(index);
        setOverIndex(index);
      },
      onPointerMove: (e) => {
        if (dragRef.current.from === null) return;
        e.preventDefault();
        const idx = indexAtPoint(e.clientX, e.clientY);
        if (idx !== null && idx !== dragRef.current.over) {
          dragRef.current.over = idx;
          setOverIndex(idx);
        }
      },
      onPointerUp: finish,
      onPointerCancel: finish,
    }),
    [indexAtPoint, finish]
  );

  return { listRef, dragIndex, overIndex, getHandleProps };
}
